// src/components/AiCoachPage.jsx
// KFX AI Coach — Trading Operating System
// Architecture: Mission Control → Profit Leak → Missions → Journey → Deep Dive
// Philosophy: Insight → Action → Progress → Reward → New Action

import { useState, useCallback, useEffect, useRef } from 'react';
import { IntelligenceGate, planCanAccess } from './FeatureGate.jsx';
import { useFeatureConfig } from '../hooks/useFeatureConfig.js';
import { api } from '../utils/api';
import PricingPage from './PricingPage.jsx';
import '../styles/aicoach.css';

// ─────────────────────────────────────────────────────────────
//  Design tokens (mirrors KFX CSS vars)
// ─────────────────────────────────────────────────────────────
const T = {
  bg:        'var(--black)',
  surface:   'var(--dark2)',
  card:      'var(--dark3)',
  cardHover: 'var(--dark4)',
  border:    'var(--border)',
  borderSub: 'var(--divider)',
  gold:      'var(--gold)',
  goldDim:   'var(--accent-gold-bg)',
  goldGlow:  'var(--gold-glow)',
  text:      'var(--text-primary)',
  textSub:   'var(--text-secondary)',
  muted:     'var(--text-muted)',
  green:     'var(--gold)',          // remapped to gold
  greenDim:  'var(--accent-gold-bg)', // remapped to gold
  red:       'var(--red)',
  redDim:    'var(--loss-bg)',
  amber:     '#f59e0b',              // no theme token yet — reads fine on light & dark
  blue:      'var(--blue)',
  purple:    'var(--gold)',          // remapped to gold
};

const GRADE_TONE = {
  'A+': 'gold', 'A': 'gold', 'A-': 'gold',
  'B+': 'gold', 'B': 'gold', 'B-': 'gold',
  'C+': 'gold', 'C': 'gold', 'C-': 'gold',
  'D':  'gold', 'F': 'gold',
};

const PRIORITY_TONE = {
  Critical: 'red', High: 'amber', Medium: 'blue', Low: 'muted',
};

// ─────────────────────────────────────────────────────────────
//  Time-aware greeting helper
// ─────────────────────────────────────────────────────────────
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// ─────────────────────────────────────────────────────────────
//  Humanise cluster/evidence text (strips raw query tokens)
// ─────────────────────────────────────────────────────────────
function humaniseClusterText(text) {
  if (!text) return text;
  return text
    .replace(/afterLoss:true/gi,       'after a loss')
    .replace(/after2Loss:true/gi,      'after 2 consecutive losses')
    .replace(/AfterLoss=true/gi,       'after a loss')
    .replace(/sym=/gi,                 '')
    .replace(/sess=/gi,                '')
    .replace(/strat=/gi,               '')
    .replace(/emo=/gi,                 '')
    .replace(/dow=/gi,                 '')
    .replace(/grade=/gi,               'grade ')
    .replace(/plan=true/gi,            'followed plan')
    .replace(/plan=false/gi,           'ignored plan')
    .replace(/movedSl=true/gi,        'moved SL')
    .replace(/movedSl=false/gi,       'kept SL')
    .replace(/\|/g,                    '·')
    .replace(/\+/g,                    ' + ')
    .replace(/n=(\d+)/g,              '$1 trades')
    .replace(/WR=([\d.]+)%/g,         '$1% win rate')
    .replace(/avgPnl=([\d.-]+)/g,     'avg $$$1')
    .replace(/totalPnl=([\d.-]+)/g,   'total $$$1')
    .replace(/\(([\d.]+)pp vs baseline\)/g, '($1% pts vs your average)')
    .replace(/\(-([\d.]+)pp vs baseline\)/g, '($1% pts below your average)')
    .replace(/\(\+([\d.]+)pp vs baseline\)/g, '($1% pts above your average)')
    .replace(/  +/g, ' ')
    .trim();
}

// ─────────────────────────────────────────────────────────────
//  Humanise leak impact strings  e.g. "-19.32pp win rate"
// ─────────────────────────────────────────────────────────────
function humaniseImpact(text) {
  if (!text) return text;
  return text
    .replace(/^-\s*([\d.]+)\s*pp\s+win\s+rate$/i,  'Win rate drops $1% when this happens')
    .replace(/^\+([\d.]+)\s*pp\s+win\s+rate$/i,     'Win rate improves $1% when this happens')
    .replace(/^-\s*([\d.]+)\s*pp$/i,               '$1% pts below your average')
    .replace(/^\+([\d.]+)\s*pp$/i,                  '$1% pts above your average')
    .replace(/-\s*([\d.]+)\s*pp\s+win\s+rate/gi,   'win rate drops $1%')
    .replace(/\+([\d.]+)\s*pp\s+win\s+rate/gi,      'win rate improves $1%')
    .replace(/([\d.]+)\s*pp\b/gi,                   '$1% pts')
    .replace(/percentage points?/gi,                '%')
    .replace(/  +/g, ' ')
    .trim();
}

// ─────────────────────────────────────────────────────────────
//  Humanise observation / "What Was Found" text
// ─────────────────────────────────────────────────────────────
function humaniseObservation(text) {
  if (!text) return text;
  return text
    .replace(/([\d.]+)\s*percentage points?\s+lower\s+than\s+the\s+overall\s+win\s+rate/gi,
             '$1% below your overall win rate')
    .replace(/([\d.]+)\s*percentage points?\s+higher\s+than\s+the\s+overall\s+win\s+rate/gi,
             '$1% above your overall win rate')
    .replace(/([\d.]+)\s*pp\b/gi, '$1% pts')
    .replace(/percentage points?/gi, '%')
    .replace(/^The trader has a win rate of ([\d.]+)%\s+when/i, 'You win $1% of trades when')
    .replace(/\bThe trader\b/g, 'You')
    .replace(/\bthe trader\b/g, 'you')
    .replace(/  +/g, ' ')
    .trim();
}

// ─────────────────────────────────────────────────────────────
//  Live countdown hook  →  "4d 06h 32m 15s"  or  null
// ─────────────────────────────────────────────────────────────
function useLiveCountdown(targetMs) {
  const [display, setDisplay] = useState(null);
  useEffect(() => {
    if (!targetMs) { setDisplay(null); return; }
    function tick() {
      const diff = targetMs - Date.now();
      if (diff <= 0) { setDisplay(null); return; }
      const d = Math.floor(diff / 864e5);
      const h = Math.floor((diff % 864e5) / 36e5);
      const m = Math.floor((diff % 36e5) / 6e4);
      const s = Math.floor((diff % 6e4) / 1e3);
      const parts = [];
      if (d) parts.push(`${d}d`);
      if (h) parts.push(`${h}h`);
      if (m) parts.push(`${m}m`);
      parts.push(`${s}s`);
      setDisplay(parts.join(' '));
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  return display;
}

// ─────────────────────────────────────────────────────────────
//  Primitive helpers
// ─────────────────────────────────────────────────────────────
const fmt = (n, pre = '$') => {
  if (n == null) return '—';
  const abs = Math.abs(n);
  const s = abs >= 1000 ? `${(abs / 1000).toFixed(1)}k` : abs.toFixed(2);
  return `${n < 0 ? '-' : '+'}${pre}${s}`;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v || 0));

function ProgressBar({ pct, tone = 'gold', height = 'md', animated = true }) {
  const p = Math.round(clamp(pct, 0, 100) / 5) * 5; // snap to nearest 5% for fixed-width classes
  return (
    <div className={`ac-progress-track ac-progress-track--${height}`}>
      <div className={`ac-progress-fill ac-progress-fill--${tone} ac-w-${p}${animated ? ' kfx-bar-fill' : ''}`} />
    </div>
  );
}

function Spinner({ size = 'md' }) {
  return <div className={`ac-spinner ac-spinner--${size}`} />;
}

function Label({ children, className = '' }) {
  return <div className={`ac-label${className ? ` ${className}` : ''}`}>{children}</div>;
}

function GradeBadge({ grade, size = 'md' }) {
  const tone = GRADE_TONE[grade] || 'muted';
  return (
    <div className={`ac-grade-badge ac-grade-badge--${size} ac-grade-badge--${tone} kfx-score-ring`}>{grade}</div>
  );
}

function EdgeRing({ score }) {
  const size = 80;
  const r = 30;
  const circ = 2 * Math.PI * r;
  const fill = (clamp(score, 0, 100) / 100) * circ;
  const tone = score >= 70 ? 'gold' : score >= 50 ? 'gold' : score >= 30 ? 'amber' : 'red';
  return (
    <div className={`ac-edge-ring ac-edge-ring--${tone}`}>
      <svg width={size} height={size} className="ac-edge-ring-svg">
        <circle cx={size/2} cy={size/2} r={r} className="ac-edge-ring-track" />
        <circle cx={size/2} cy={size/2} r={r} className="ac-edge-ring-fill" strokeDasharray={`${fill} ${circ}`} />
      </svg>
      <div className="ac-edge-ring-label">
        <div className="ac-edge-ring-score">{score}</div>
        <div className="ac-edge-ring-tag">EDGE</div>
      </div>
    </div>
  );
}

function StatChip({ label, value, positive = null }) {
  const valueClass = positive === null ? '' : positive ? ' ac-stat-positive' : ' ac-stat-negative';
  return (
    <div className="ac-stat-chip">
      <span className="ac-stat-chip-label">{label}</span>
      <span className={`ac-stat-chip-value${valueClass}`}>{value}</span>
    </div>
  );
}

// Collapsible section container
function Disclosure({ title, icon, children, defaultOpen = false, badge = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ac-disclosure">
      <div
        className={`ac-disclosure-header kfx-disclosure-header${open ? ' ac-open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="ac-disclosure-icon">{icon}</span>
        <span className="ac-disclosure-title">{title}</span>
        {badge && <span className="ac-disclosure-badge">{badge}</span>}
        <span className="ac-disclosure-caret">▾</span>
      </div>
      {open && <div className="ac-disclosure-body">{children}</div>}
    </div>
  );
}

// Premium gate overlay
function PremiumGate({ feature, onUpgrade }) {
  return (
    <div className="ac-premium-gate">
      <div className="ac-premium-gate-icon">★</div>
      <div className="ac-premium-gate-title">Premium Feature</div>
      <div className="ac-premium-gate-desc">{feature}</div>
      <button className="kfx-btn-primary ac-btn-primary" onClick={onUpgrade}>
        Upgrade to Premium
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Mission Control Hero
// ─────────────────────────────────────────────────────────────
function MissionControlHero({ report, name, plan }) {
  const dna = report.tradingDNAProfile || {};
  const grade = report.traderGrade || '—';
  const edge = report.edgeScore || 0;
  const leak = report.behavioralMistakes?.[0];
  const strength = report.strengths?.[0];
  const topInsight = report.insights?.find(i => i.priority === 'Critical') || report.insights?.[0];
  const whatif = report.whatIfAnalysis?.[0];
  const potentialGain = whatif?.vsActualNet ? Math.abs(whatif.vsActualNet) : null;

  return (
    <div className="ac-hero-root">
      {/* Gold atmosphere */}
      <div className="ac-hero-glow-line" />
      <div className="ac-hero-glow-radial" />

      <div className="kfx-hero">
        <div className="ac-hero-inner-max">

          {/* Greeting + DNA pill */}
          <div className="ac-hero-greeting-row">
            <div className="ac-hero-kicker">
              KFX TRADING OS
            </div>
            {dna.traderType && (
              <div className="ac-hero-dna-pill">
                {dna.traderType}
              </div>
            )}
          </div>

          <div className="kfx-hero-inner">
            {/* Left: greeting + primary KPIs */}
            <div className="ac-hero-left">
              <h1 className="ac-hero-title">
                {name ? `${getGreeting()}, ${name.split(' ')[0]}.` : 'Your Trading OS'}
              </h1>
              <p className="ac-hero-subtitle">
                {dna.dominantStrength || (Array.isArray(report.executiveSummary) ? report.executiveSummary[0] : report.executiveSummary)?.split('.')[0] + '.'}
              </p>

              {/* Critical action card */}
              {topInsight && (
                <div className="ac-hero-priority-card">
                  <Label className="ac-mb-6">Today's Priority Action</Label>
                  <div className="ac-hero-priority-text">
                    {(Array.isArray(topInsight.recommendation) ? topInsight.recommendation[0] : topInsight.recommendation)?.split('\n')[0]?.replace(/^\d+\.\s*/, '') || topInsight.title}
                  </div>
                </div>
              )}
            </div>

            {/* Right: score cluster */}
            <div className="ac-hero-right">
              {/* Grade + Edge */}
              <div className="ac-hero-scores-row">
                <div className="ac-center">
                  <Label className="ac-mb-8 ac-center">Grade</Label>
                  <GradeBadge grade={grade} size="lg" />
                </div>
                <div className="ac-center">
                  <Label className="ac-mb-8 ac-center">Edge Score</Label>
                  <EdgeRing score={edge} />
                </div>
              </div>

              {/* Potential gain */}
              {potentialGain != null && (
                <div className="ac-hero-gain">
                  <Label className="ac-mb-4">Monthly Profit Left on Table</Label>
                  <div className="ac-hero-gain-value">
                    +${potentialGain.toFixed(0)}
                  </div>
                  <div className="ac-hero-gain-sub">{whatif?.scenario}</div>
                </div>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="kfx-mc-grid ac-hero-kpi-grid">
            {[
              {
                label: 'Biggest Leak',
                value: leak?.mistake || dna.dominantWeakness?.split(' ').slice(0, 3).join(' ') || '—',
                tone: 'red',
                icon: '↘',
                sub: leak?.cost ? `Cost: ${humaniseImpact(leak.cost)}` : null,
              },
              {
                label: 'Biggest Strength',
                value: strength?.title || dna.dominantStrength?.split(' ').slice(0, 3).join(' ') || '—',
                tone: 'gold',
                icon: '↗',
                sub: strength?.evidence ? strength.evidence.slice(0, 40) + '…' : null,
              },
              {
                label: 'Trader DNA',
                value: dna.traderType || '—',
                tone: 'gold',
                icon: '◈',
                sub: dna.edgeSource ? dna.edgeSource.slice(0, 40) + '…' : null,
              },
              {
                label: 'Risk Rating',
                value: report.riskAnalysis?.riskRating || '—',
                tone: ({ Conservative: 'gold', Balanced: 'gold', Aggressive: 'amber', Reckless: 'red' })[report.riskAnalysis?.riskRating] || 'muted',
                icon: '⬡',
                sub: null,
              },
            ].map(({ label, value, tone, icon, sub }) => (
              <div key={label} className={`ac-hero-kpi-card ac-hero-kpi-card--${tone}`}>
                <div className="ac-hero-kpi-head">
                  <span className="ac-hero-kpi-icon">{icon}</span>
                  <Label>{label}</Label>
                </div>
                <div className={`ac-hero-kpi-value${sub ? ' ac-has-sub' : ''}`}>{value}</div>
                {sub && <div className="ac-hero-kpi-sub">{sub}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Profit Leak Detector (signature feature)
// ─────────────────────────────────────────────────────────────
function ProfitLeakDetector({ report, plan, onUpgrade, requiredPlans, featureLabel }) {
  const isPremium = planCanAccess(plan, requiredPlans('ai_recovery_plan'));
  const leak = report.behavioralMistakes?.[0];
  const insights = report.insights?.filter(i => i.priority === 'Critical' || i.priority === 'High') || [];
  const topLeak = insights[0];

  if (!topLeak && !leak) return null;

  const title = topLeak?.title || leak?.mistake || 'Unknown Leak';
  const cost = leak?.cost || topLeak?.evidence;
  const _rawFix = leak?.fix || topLeak?.recommendation;
  const fix = Array.isArray(_rawFix) ? _rawFix.join('\n') : _rawFix;
  const freq = leak?.frequency;
  const confidence = report.confidenceScore;

  return (
    <div>
      {/* Signature: primary leak spotlight */}
      <div className="ac-leak-spotlight">
        <div className="ac-leak-head">
          <div className="ac-leak-dot" />
          <Label className="ac-c-red">Your Biggest Profit Leak</Label>
          {confidence && <span className="ac-leak-confidence">Confidence: {confidence}%</span>}
        </div>

        <h2 className="ac-leak-title">
          {title}
        </h2>

        <div className="kfx-leak-grid">
          {/* Left: impact */}
          <div>
            {cost && (
              <div className="ac-leak-block">
                <Label className="ac-mb-6">Estimated Impact</Label>
                <div className="ac-leak-text">{humaniseImpact(cost)}</div>
              </div>
            )}
            {freq && (
              <div className="ac-leak-block">
                <Label className="ac-mb-6">How Often</Label>
                <div className="ac-leak-text">{freq}</div>
              </div>
            )}
            {topLeak?.observation && (
              <div>
                <Label className="ac-mb-6">What Was Found</Label>
                <div className="ac-leak-text">{humaniseObservation(topLeak.observation)}</div>
              </div>
            )}
          </div>

          {/* Right: recovery plan */}
          <div>
            {isPremium ? (
              <div>
                <Label className="ac-mb-10">Recovery Plan</Label>
                {fix && (
                  <div className="ac-leak-recovery-box">
                    {fix}
                  </div>
                )}
                {topLeak?.expectedImpact && (
                  <div className="ac-leak-fixed-box">
                    <Label className="ac-c-gold ac-mb-4">If Fixed</Label>
                    <div className="ac-leak-fixed-text">{topLeak.expectedImpact}</div>
                  </div>
                )}
              </div>
            ) : (
              <IntelligenceGate
                userPlan={plan}
                requiredPlans={requiredPlans('ai_recovery_plan')}
                featureKey="ai_recovery_plan"
                label={featureLabel('ai_recovery_plan')}
                onUpgrade={onUpgrade}
                revealHeight={150}
              >
                <div>
                  <Label className="ac-mb-10">Recovery Plan</Label>
                  <div className="ac-leak-recovery-box ac-leak-recovery-box--gated">
                    {fix || 'Premium recovery plan content available here. Upgrade to see the complete recovery roadmap and expected profit improvement.'}
                  </div>
                </div>
              </IntelligenceGate>
            )}
          </div>
        </div>
      </div>

      {/* Secondary leaks — compact list */}
      {insights.slice(1, 4).map((ins, i) => {
        const tone = PRIORITY_TONE[ins.priority] || 'muted';
        return (
        <div key={ins.id || i} className={`kfx-insight ac-leak-secondary ac-leak-secondary--${tone}`}>
          <div className="ac-leak-secondary-dot" />
          <div className="ac-leak-secondary-body">
            <div className="ac-leak-secondary-title">{ins.title}</div>
            <div className="ac-leak-secondary-evidence">{humaniseClusterText(ins.evidence)}</div>
          </div>
          <div className="ac-leak-secondary-badge">
            {ins.priority}
          </div>
        </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Mission hint copy — shown instead of manual log button
// ─────────────────────────────────────────────────────────────
const MISSION_HINTS = {
  RR_DISCIPLINE:      'Auto-counts trades where actual R:R ≥ 1:2',
  NY_SESSION_FOCUS:   'Auto-counts trades logged in the New York session',
  PLAN_ADHERENCE:     'Auto-counts trades where "Followed Plan" is checked',
  GRADE_A_SETUPS:     'Auto-counts trades with Setup Grade = A',
  WIN_STREAK_BUILDER: 'Auto-counts winning trades (positive P&L)',
  JOURNALING_HABIT:   'Auto-counts trades with notes (20+ characters)',
  SCREENSHOT_HABIT:   'Auto-counts trades with a screenshot attached',
};

function getMissionHint(type) {
  return MISSION_HINTS[type] ?? 'Log trades in your journal to progress automatically';
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Weekly Mission System
// ─────────────────────────────────────────────────────────────
function MissionSystem({ report, savedMissions, onMissionUpdate }) {
  // Build missions from 30-day plan + top insights
  const plan = report.thirtyDayPlan || [];
  const week1 = plan[0];
  const insights = report.insights || [];

  // Map insight priority / weak area to a known RULES key so the hint resolves
  const INSIGHT_TYPE_MAP = {
    'RR':          'RR_DISCIPLINE',
    'RR_DISCIPLINE': 'RR_DISCIPLINE',
    'SESSION':     'NY_SESSION_FOCUS',
    'PLAN':        'PLAN_ADHERENCE',
    'GRADE':       'GRADE_A_SETUPS',
    'WIN':         'WIN_STREAK_BUILDER',
    'JOURNAL':     'JOURNALING_HABIT',
    'SCREENSHOT':  'SCREENSHOT_HABIT',
  };

  const missionDefs = [
    ...(week1 ? [{
      id: 'week1_focus',
      title: week1.focus,
      description: week1.actions?.[0] || '',
      target: 10,
      impact: week1.successMetric,
      reward: '+5 Edge Score',
      type: 'focus',
      missionType: week1.missionType || null,
    }] : []),
    ...insights.filter(i => i.priority === 'Critical' || i.priority === 'High').slice(0, 3).map(ins => ({
      id: ins.id || ins.title,
      title: ins.title,
      description: (Array.isArray(ins.recommendation) ? ins.recommendation[0] : ins.recommendation)?.split('\n')[0]?.replace(/^\d+\.\s*/, '') || ins.title,
      target: 10,
      impact: ins.expectedImpact,
      reward: '+3 Edge Score',
      type: 'insight',
      missionType: ins.missionType || INSIGHT_TYPE_MAP[ins.category?.toUpperCase()] || null,
    })),
  ].slice(0, 4);

  return (
    <div>
      <div className="kfx-missions-grid">
        {missionDefs.map((m, i) => {
          const saved = savedMissions?.[m.id] || { progress: 0, completed: false };
          const pct = Math.min(100, (saved.progress / m.target) * 100);
          const isActive = i === 0 && !saved.completed;
          return (
            <div
              key={m.id}
              className={`ac-mission-card${isActive ? ' kfx-mission-active ac-mission-card--active' : ' kfx-lift'}${saved.completed ? ' ac-mission-card--done' : ''}`}
            >
              {/* Mission header */}
              <div className="ac-mission-head">
                <div>
                  <Label className={`ac-mb-6${isActive ? ' ac-c-gold' : ''}`}>
                    {saved.completed ? '✓ Completed' : `Mission ${i + 1}`}
                  </Label>
                  <div className={`ac-mission-title${saved.completed ? ' ac-c-gold' : ''}`}>
                    {m.title}
                  </div>
                </div>
                {saved.completed && (
                  <div className="ac-mission-trophy">🏆</div>
                )}
              </div>

              {m.description && (
                <div className="ac-mission-desc">{m.description}</div>
              )}

              {/* Progress */}
              <div className="ac-mission-progress">
                <div className="ac-mission-progress-row">
                  <span className="ac-mission-progress-label">Progress</span>
                  <span className="ac-mission-progress-value">
                    {saved.progress}/{m.target} trades
                  </span>
                </div>
                <ProgressBar pct={pct} tone="gold" height="md" />
              </div>

              {/* Impact + reward */}
              <div className="ac-mission-tags">
                {m.impact && (
                  <div className="ac-mission-tag-impact">
                    {m.impact}
                  </div>
                )}
                <div className="ac-mission-tag-reward">
                  {m.reward}
                </div>
              </div>

              {/* Auto-tracking hint — replaces manual Log Trade button */}
              <div className="ac-mission-hint">
                ◈ {getMissionHint(m.missionType)}
              </div>
            </div>
          );
        })}
      </div>

      {/* 30-day plan weeks 2-4 in compact view */}
      {plan.slice(1).length > 0 && (
        <Disclosure title="30-Day Coaching Plan" icon="◷" badge={`${plan.length} weeks`} defaultOpen={false}>
          <div className="ac-plan-list">
            {plan.map((w, i) => (
              <div key={i} className="ac-plan-week-card">
                <div className="ac-plan-week-head">
                  <span className="ac-plan-week-num">WEEK {w.week}</span>
                  <span className="ac-plan-week-focus">{w.focus}</span>
                  {w.successMetric && <span className="ac-plan-week-metric">{w.successMetric}</span>}
                </div>
                <div className="ac-plan-actions">
                  {(w.actions || []).map((a, j) => (
                    <div key={j} className="ac-plan-action">
                      <span className="ac-plan-action-num">{j + 1}.</span>
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Disclosure>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: WOW — If You Changed One Thing
// ─────────────────────────────────────────────────────────────
function WowSection({ report }) {
  const whatifs = report.whatIfAnalysis || [];
  const best = [...whatifs].sort((a, b) => Math.abs(b.vsActualNet || 0) - Math.abs(a.vsActualNet || 0))[0];
  if (!best) return null;

  const gain = best.vsActualNet ? Math.abs(best.vsActualNet) : null;
  const wrGain = best.simulatedWR != null && report.dataset?.wr != null
    ? (best.simulatedWR - report.dataset.wr).toFixed(1)
    : null;

  return (
    <div className="ac-wow-card">
      <Label className="ac-c-amber ac-mb-12">If You Changed One Thing</Label>
      <h2 className="ac-wow-title">
        {best.scenario}
      </h2>
      <p className="ac-wow-verdict">{best.verdict}</p>

      <div className="kfx-three-col">
    {gain != null && (
  <div className="ac-wow-stat">
    <Label className="ac-mb-6">Monthly Gain</Label>
    <div className="ac-wow-stat-value ac-wow-stat-value--gold">
      +${gain.toFixed(0)}
    </div>
  </div>
)}
{wrGain != null && (
  <div className="ac-wow-stat">
    <Label className="ac-mb-6">Win Rate</Label>
    <div className={`ac-wow-stat-value ac-wow-stat-value--${parseFloat(wrGain) >= 0 ? 'gold' : 'red'}`}>
      {parseFloat(wrGain) >= 0 ? '+' : ''}{wrGain}%
    </div>
  </div>
)}
        <div className="ac-wow-stat">
          <Label className="ac-mb-6">Action This Week</Label>
          <div className="ac-wow-action-text">{best.action || '—'}</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Benchmarking
// ─────────────────────────────────────────────────────────────
function BenchmarkSection({ report, plan, onUpgrade, requiredPlans, featureLabel }) {
  const ds = report.dataset || {};
  const bm = ds.benchmarks || {};

  // Use AI-generated top10 thresholds when available, else hide that metric
  const benchmarks = [
    bm.winRate      && { label: 'Win Rate',      yours: ds.wr,    top: bm.winRate.top10,      unit: '%',  higher: true,  context: bm.winRate.context },
    bm.profitFactor && { label: 'Profit Factor', yours: ds.pf,    top: bm.profitFactor.top10, unit: '',   higher: true,  context: bm.profitFactor.context },
    bm.avgRR        && { label: 'Avg R:R',        yours: ds.avgRR, top: bm.avgRR.top10,        unit: '',   higher: true,  context: bm.avgRR.context },
    bm.maxDrawdown  && { label: 'Max Drawdown',  yours: ds.maxDD, top: bm.maxDrawdown.top10,  unit: '',   higher: false, context: bm.maxDrawdown.context },
  ].filter(b => b && b.yours != null && b.top != null);

  if (!benchmarks.length) return null;

  return (
    <IntelligenceGate
      userPlan={plan}
      requiredPlans={requiredPlans('ai_benchmarks')}
      featureKey="ai_benchmarks"
      label={featureLabel('ai_benchmarks')}
      onUpgrade={onUpgrade}
      revealHeight={150}
      surfaceColor={T.bg}
    >
    <div className="ac-benchmark-card">
      <div className="ac-benchmark-head">
        <Label>Performance Targets</Label>
      </div>
      <div className="ac-benchmark-list">
        {benchmarks.map(b => {
          const gap = b.higher
            ? (b.top - b.yours).toFixed(1)
            : (b.yours - b.top).toFixed(1);
          const ahead = b.higher ? b.yours >= b.top : b.yours <= b.top;
          return (
            <div key={b.label}>
              <div className="ac-benchmark-row-head">
                <div>
                  <span className="ac-benchmark-label">{b.label}</span>
                  {b.context && (
                    <div className="ac-benchmark-context">{b.context}</div>
                  )}
                </div>
                <div className="ac-benchmark-right">
                  <span className="ac-benchmark-target">Target: <span className="ac-benchmark-target-value">{b.top}{b.unit}</span></span>
                  <span className={`ac-benchmark-yours ac-benchmark-yours--${ahead ? 'gold' : 'red'}`}>
                    You: {b.yours}{b.unit}
                  </span>
                </div>
              </div>
              <div className="ac-benchmark-bar-wrap">
                <ProgressBar pct={(b.yours / b.top) * 100} tone={ahead ? 'gold' : 'amber'} height="sm" />
                {/* Target line */}
                <div className="ac-benchmark-target-line" />
              </div>
              {!ahead && parseFloat(gap) > 0 && (
                <div className="ac-benchmark-gap">Gap: {gap}{b.unit} to reach target</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    </IntelligenceGate>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: What-If Simulations
// ─────────────────────────────────────────────────────────────
function WhatIfSection({ report, plan, onUpgrade, requiredPlans, featureLabel }) {
  const scenarios = report.whatIfAnalysis || [];
  const isPremium = planCanAccess(plan, requiredPlans('ai_whatif_scenarios'));
  const visible = scenarios;

  if (!scenarios.length) return null;

  const list = (
    <div className="ac-whatif-list">
        {visible.map((s, i) => {
          const gain = s.vsActualNet;
          const isPos = gain != null && gain > 0;
          return (
            <div key={i} className={`kfx-lift ac-whatif-card ac-whatif-card--${isPos ? 'gold' : 'amber'}`}>
              <div className="ac-whatif-body">
                <div className="ac-whatif-scenario">{s.scenario}</div>
                <div className="ac-whatif-verdict">{s.verdict}</div>
              </div>
              <div className="ac-whatif-stats">
                {gain != null && (
                  <div className="ac-whatif-stat">
                    <Label className="ac-mb-2">P&L Impact</Label>
                    <div className={`ac-whatif-stat-value ac-whatif-stat-value--${isPos ? 'gold' : 'red'}`}>
                      {gain > 0 ? '+' : ''}{gain?.toFixed(2)}
                    </div>
                  </div>
                )}
                {s.simulatedWR != null && (
                  <div className="ac-whatif-stat">
                    <Label className="ac-mb-2">Sim WR</Label>
                    <div className="ac-whatif-stat-value ac-whatif-stat-value--gold">{s.simulatedWR}%</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );

  if (isPremium) return list;

  return (
    <IntelligenceGate
      userPlan={plan}
      requiredPlans={requiredPlans('ai_whatif_scenarios')}
      featureKey="ai_whatif_scenarios"
      label={featureLabel('ai_whatif_scenarios')}
      onUpgrade={onUpgrade}
      revealHeight={150}
      surfaceColor={T.bg}
      ctaSubline={scenarios.length > 2 ? `${scenarios.length - 2} more scenarios hidden` : undefined}
    >
      {list}
    </IntelligenceGate>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: DNA Profile
// ─────────────────────────────────────────────────────────────
function DnaProfile({ report, plan, onUpgrade, requiredPlans, featureLabel }) {
  const dna = report.tradingDNAProfile || {};
  const perf = report.expectedFuturePerformance || {};
  if (!dna.traderType) return null;

  return (
    <IntelligenceGate
      userPlan={plan}
      requiredPlans={requiredPlans('ai_dna_profile')}
      featureKey="ai_dna_profile"
      label={featureLabel('ai_dna_profile')}
      onUpgrade={onUpgrade}
      revealHeight={150}
      surfaceColor={T.bg}
    >
    <div className="kfx-two-col">
      <div className="ac-dna-card">
        <Label className="ac-mb-14">Trading DNA</Label>
        <div className="ac-dna-type">{dna.traderType}</div>
        {dna.dominantStrength && (
          <div className="ac-dna-block">
            <div className="ac-dna-tag ac-dna-tag--gold">Strength</div>
            <div className="ac-dna-text">{dna.dominantStrength}</div>
          </div>
        )}
        {dna.dominantWeakness && (
          <div className="ac-dna-block">
            <div className="ac-dna-tag ac-dna-tag--red">Weakness</div>
            <div className="ac-dna-text">{dna.dominantWeakness}</div>
          </div>
        )}
        {dna.edgeSource && (
          <div>
            <div className="ac-dna-tag ac-dna-tag--gold">Edge Source</div>
            <div className="ac-dna-text">{dna.edgeSource}</div>
          </div>
        )}
      </div>

      <div className="ac-dna-card">
        <Label className="ac-mb-14">Performance Forecast</Label>
        {perf.optimisticCase && (
          <div className="ac-forecast-box ac-forecast-box--optimistic">
            <div className="ac-forecast-tag ac-forecast-tag--gold">Optimistic</div>
            <div className="ac-forecast-text">{perf.optimisticCase}</div>
          </div>
        )}
        {perf.baseCase && (
          <div className="ac-forecast-box ac-forecast-box--base">
            <div className="ac-forecast-tag ac-forecast-tag--gold">Base Case</div>
            <div className="ac-forecast-text">{perf.baseCase}</div>
          </div>
        )}
        {perf.keyLeverage && (
          <div className="ac-forecast-box ac-forecast-box--leverage">
            <div className="ac-forecast-tag ac-forecast-tag--amber">Highest Leverage Change</div>
            <div className="ac-forecast-text">{perf.keyLeverage}</div>
          </div>
        )}
      </div>
    </div>
    </IntelligenceGate>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Deep Dive — Insights
// ─────────────────────────────────────────────────────────────
function InsightsList({ insights, plan, onUpgrade, requiredPlans, featureLabel }) {
  const [expanded, setExpanded] = useState(null);
  const isPremium = planCanAccess(plan, requiredPlans('ai_insights_full'));

  if (!insights?.length) return null;

  const visible = insights;

  const list = (
    <div>
      {visible.map((ins, i) => {
        const isOpen = expanded === i;
        const tone = PRIORITY_TONE[ins.priority] || 'muted';
        return (
          <div
            key={ins.id || i}
            className={`kfx-insight ac-insight-card ac-insight-card--${tone}`}
            onClick={() => setExpanded(isOpen ? null : i)}
          >
            <div className="ac-insight-head">
              <div className="ac-insight-dot" />
              <div className="ac-insight-body">
                <div className="ac-insight-title-row">
                  <span className="ac-insight-title">{ins.title}</span>
                  <span className="ac-insight-category">{ins.category}</span>
                </div>
                <div className="ac-insight-evidence">{humaniseClusterText(ins.evidence)}</div>
              </div>
              <div className="ac-insight-priority-badge">
                {ins.priority}
              </div>
            </div>
            {isOpen && (
              <div className="ac-insight-expanded">
                {ins.observation && (
                  <div className="ac-insight-section">
                    <Label className="ac-mb-4">Observation</Label>
                    <p className="ac-insight-text">{ins.observation}</p>
                  </div>
                )}
                {ins.interpretation && (
                  <div className="ac-insight-section">
                    <Label className="ac-mb-4">Why This Happens</Label>
                    <p className="ac-insight-text">{ins.interpretation}</p>
                  </div>
                )}
                {ins.recommendation && (
                  <div className="ac-insight-action-box">
                    <Label className="ac-c-gold ac-mb-6">Action Steps</Label>
                    <pre className="ac-insight-action-text">{Array.isArray(ins.recommendation) ? ins.recommendation.join('\n') : ins.recommendation}</pre>
                  </div>
                )}
                {ins.expectedImpact && (
                  <div className="ac-insight-impact-box">
                    <Label className="ac-c-gold ac-mb-2">Expected Impact</Label>
                    <div className="ac-insight-impact-text">{ins.expectedImpact}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (isPremium) return list;

  return (
    <IntelligenceGate
      userPlan={plan}
      requiredPlans={requiredPlans('ai_insights_full')}
      featureKey="ai_insights_full"
      label={featureLabel('ai_insights_full')}
      onUpgrade={onUpgrade}
      revealHeight={150}
      surfaceColor={T.bg}
      ctaSubline={insights.length > 4 ? `${insights.length - 4} more insights locked` : undefined}
    >
      {list}
    </IntelligenceGate>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Market & Session analysis
// ─────────────────────────────────────────────────────────────
function MarketSection({ report }) {
  const mkt = report.marketAnalysis || {};
  const sess = report.sessionAnalysis || {};
  const risk = report.riskAnalysis || {};

  return (
    <div className="ac-market-stack">
      {/* Market */}
      {(mkt.bestPair || mkt.worstPair) && (
        <div className="ac-market-card">
          <Label className="ac-mb-14">Market Analysis</Label>
          <div className="kfx-two-col">
            {mkt.bestPair && <StatChip label="Best Pair" value={mkt.bestPair} positive={true} />}
            {mkt.worstPair && <StatChip label="Worst Pair" value={mkt.worstPair} positive={false} />}
            {mkt.bestMarketCondition && <StatChip label="Best Condition" value={mkt.bestMarketCondition} positive={true} />}
          </div>
          {mkt.confluenceTagInsights && (
            <div className="ac-market-note">
              <Label className="ac-mb-4">Confluence Tags</Label>
              <div className="ac-market-note-text">{mkt.confluenceTagInsights}</div>
            </div>
          )}
        </div>
      )}

      {/* Sessions */}
      {(sess.bestSession || sess.worstSession) && (
        <div className="ac-market-card">
          <Label className="ac-mb-14">Session & Timing</Label>
          <div className="kfx-two-col">
            {sess.bestSession && <StatChip label="Best Session" value={sess.bestSession} positive={true} />}
            {sess.worstSession && <StatChip label="Worst Session" value={sess.worstSession} positive={false} />}
            {sess.bestDuration && <StatChip label="Optimal Hold Time" value={sess.bestDuration} />}
            {sess.dowPattern && <StatChip label="DOW Pattern" value={sess.dowPattern} />}
          </div>
          {sess.timeRecommendation && (
            <div className="ac-market-note ac-market-note--gold">
              <Label className="ac-c-gold ac-mb-4">Schedule Recommendation</Label>
              <div className="ac-market-note-text">{sess.timeRecommendation}</div>
            </div>
          )}
        </div>
      )}

      {/* Risk */}
      {risk.summary && (
        <div className="ac-market-card">
          <Label className="ac-mb-14">Risk Analysis</Label>
          <div className="ac-risk-summary">{risk.summary}</div>
          <div className="kfx-two-col">
            {risk.maxDrawdown && <StatChip label="Max Drawdown" value={risk.maxDrawdown} />}
            {risk.positionSizing && <StatChip label="Sizing" value={risk.positionSizing} />}
            {risk.worstStreak && <StatChip label="Worst Streak" value={risk.worstStreak} positive={false} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Weekly Digest
// ─────────────────────────────────────────────────────────────
function WeeklyDigestSection({ weekly, onFetch, loading, plan }) {
  if (loading) {
    return (
      <div className="ac-digest-loading">
        <Spinner size="md" />
        <div className="ac-digest-loading-text">Analysing your week…</div>
      </div>
    );
  }

  if (!weekly) {
    return (
      <div className="ac-digest-empty">
        <div className="ac-digest-empty-body">
          <div className="ac-digest-empty-title">Weekly Digest</div>
          <div className="ac-digest-empty-desc">
            Compare this week vs last — win rate, P&L delta, dominant emotion, and an AI coaching note.
          </div>
        </div>
        <button className="kfx-btn-primary ac-btn-primary" onClick={onFetch}>
          ◈ Load Weekly Digest
        </button>
      </div>
    );
  }

  if (weekly.insufficient) {
    return <div className="ac-digest-insufficient">{weekly.message}</div>;
  }

  const { thisWeek, lastWeek, weekOverWeek, coachingNote } = weekly;

  return (
    <div>
      {/* Delta strip */}
      <div className="kfx-three-col ac-digest-delta-strip">
        {[
          { label: 'Net P&L Δ', val: weekOverWeek?.netDelta },
          { label: 'Win Rate Δ', val: weekOverWeek?.wrDelta ? `${weekOverWeek.wrDelta}pp` : '—' },
          { label: 'Avg Trade Δ', val: weekOverWeek?.avgDelta },
        ].map(({ label, val }) => {
          const isPos = typeof val === 'string' && val.startsWith('+');
          const isNeg = typeof val === 'string' && val.startsWith('-');
          const tone = isPos ? 'gold' : isNeg ? 'red' : 'muted';
          return (
            <div key={label} className={`ac-digest-delta-card ac-digest-delta-card--${tone}`}>
              <Label className="ac-mb-6">{label}</Label>
              <div className={`ac-digest-delta-value ac-digest-delta-value--${tone}`}>{val || '—'}</div>
            </div>
          );
        })}
      </div>

      {/* This/last week side by side */}
      <div className="kfx-two-col ac-digest-period-row">
        {[{ period: 'This Week', data: thisWeek, tone: 'gold' }, { period: 'Last Week', data: lastWeek, tone: 'muted' }].map(({ period, data, tone }) => data ? (
          <div key={period} className={`ac-digest-period-card ac-digest-period-card--${tone}`}>
            <Label className={`ac-mb-10${period === 'This Week' ? ' ac-c-gold' : ''}`}>{period}</Label>
            <StatChip label="Trades" value={data.n} />
            <StatChip label="Net P&L" value={data.net >= 0 ? `+$${data.net}` : `-$${Math.abs(data.net)}`} positive={data.net > 0} />
            <StatChip label="Win Rate" value={`${data.wr}%`} positive={data.wr >= 50} />
            {data.planRate != null && <StatChip label="Plan Rate" value={`${data.planRate}%`} positive={data.planRate >= 70} />}
            {data.topEmotion && <StatChip label="Dominant Emotion" value={data.topEmotion.emotion} />}
          </div>
        ) : (
          <div key={period} className="ac-digest-period-empty">
            No data for {period.toLowerCase()}
          </div>
        ))}
      </div>

      {coachingNote && (
        <div className="ac-digest-note">
          <Label className="ac-mb-8 ac-c-gold">◈ Coaching Note</Label>
          <p className="ac-digest-note-text">{coachingNote}</p>
        </div>
      )}

      <div className="ac-digest-refresh-row">
        <button className="kfx-btn-ghost ac-btn-ghost" onClick={onFetch}>↺ Refresh</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  SECTION: Empty / Generate State
// ─────────────────────────────────────────────────────────────
function GenerateState({ onGenerate, loading, error, canGenerate, waitLabel, nextAllowedAt, plan, onUpgrade, requiredPlans }) {
  const countdown = useLiveCountdown(nextAllowedAt);
  return (
    <div className="ac-generate-root">
      <div className="ac-generate-icon kfx-mission-active">◈</div>

      <div>
        <h2 className="ac-generate-title">
          Your Trading Operating System
        </h2>
        <p className="ac-generate-desc">
          Generate your first AI report. We'll analyse every trade, surface your biggest profit leak,
          build you a mission plan, and track your progress toward consistent profitability.
        </p>
      </div>

      {error && (
        <div className="ac-generate-error">
          {error}
        </div>
      )}

      {canGenerate ? (
        <button
          className="kfx-btn-primary ac-btn-primary ac-btn-primary--lg"
          onClick={onGenerate}
          disabled={loading}
        >
          {loading ? <><Spinner size="sm" /> Analysing your trades…</> : '◈ Generate My Trading OS'}
        </button>
      ) : (
        <div className="ac-generate-wait">
          <div className="ac-generate-countdown">
            Next report in <span className="ac-generate-countdown-value">{countdown ?? waitLabel}</span>
          </div>
          {!planCanAccess(plan, requiredPlans('ai_unlimited_reports')) && (
            <button onClick={onUpgrade} className="kfx-btn-primary ac-btn-primary ac-btn-primary--sm">
              ★ Upgrade for Unlimited Reports
            </button>
          )}
        </div>
      )}

      {/* Feature preview cards */}
      <div className="kfx-three-col ac-generate-features">
        {[
          { icon: '↘', title: 'Profit Leak Detector', desc: 'Pinpoints the exact behavior costing you money' },
          { icon: '◎', title: 'Weekly Missions', desc: 'Active goals with progress tracking and rewards' },
          { icon: '◈', title: 'Edge Score Evolution', desc: 'Track your improvement over time with benchmarks' },
        ].map(({ icon, title, desc }) => (
          <div key={title} className="ac-generate-feature-card">
            <div className="ac-generate-feature-icon">{icon}</div>
            <div className="ac-generate-feature-title">{title}</div>
            <div className="ac-generate-feature-desc">{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Loading skeleton
// ─────────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div className="ac-skeleton-root">
      <Spinner size="lg" />
      <div className="ac-skeleton-label">
        <div className="ac-skeleton-title">Analysing your trades</div>
        <div className="ac-skeleton-sub">Computing 30+ metrics…</div>
      </div>
      <div className="ac-skeleton-bars">
        <div className="kfx-shimmer ac-skeleton-bar ac-skeleton-bar--hero" />
        <div className="kfx-shimmer ac-skeleton-bar ac-skeleton-bar--wide" />
        <div className="kfx-shimmer ac-skeleton-bar ac-skeleton-bar--narrow" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  TABS config
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'mission',   label: '◈ Mission Control' },
  { id: 'missions',  label: '◎ Missions'         },
  { id: 'insights',  label: '⚠ Deep Insights'    },
  { id: 'markets',   label: '◷ Markets & Sessions'},
  { id: 'weekly',    label: '◫ Weekly Digest'     },
  { id: 'history',   label: '◑ Report History'    },
];

// ─────────────────────────────────────────────────────────────
//  MAIN: AiCoachPage
// ─────────────────────────────────────────────────────────────
export default function AiCoachPage({ user, activeAccountId, onUpgrade }) {
  const { requiredPlans, featureLabel } = useFeatureConfig();
  const [report, setReport]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState(null);
  const [canGenerate, setCanGenerate] = useState(true);
  const [waitLabel, setWaitLabel] = useState(null);
  const [nextAllowedAt, setNextAllowedAt] = useState(null);
  const mainCountdown = useLiveCountdown(nextAllowedAt);
  const [plan, setPlan]           = useState('free');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [activeTab, setActiveTab] = useState('mission');

  // Report history
  const [historyReports, setHistoryReports]     = useState(null);  // null = not yet fetched
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [historyError, setHistoryError]         = useState(null);
  const [viewingHistoric, setViewingHistoric]   = useState(null);  // { id, createdAt } of the loaded historic report

  // Mission progress — persisted to DB (stored in latest AiCoachReport.reportJson)
  const [missions, setMissions] = useState({});

  // Weekly digest
  const [weekly, setWeekly]         = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyError, setWeeklyError]   = useState(null);

  const accountParam = activeAccountId ? `?accountId=${activeAccountId}` : '';
  const userName = user?.name || '';

  // ── Load saved missions from report (returned with report on load) ──
  // missionState is stored inside reportJson and returned with the report
  useEffect(() => {
    if (report?.missionState) {
      setMissions(report.missionState);
    }
  }, [report?.missionState]);

  const saveMission = useCallback(async (id, progress, target) => {
    setMissions(prev => {
      const updated = { ...prev, [id]: { progress, completed: progress >= target } };
      // Fire-and-forget DB persist — no await, UI stays snappy
      api('/ai/missions', {
        method: 'PATCH',
        body: JSON.stringify({
          missionState: updated,
          ...(activeAccountId ? { accountId: activeAccountId } : {}),
        }),
      }).catch(() => {}); // silent fail — not critical
      return updated;
    });
  }, [activeAccountId]);

  // ── Load report on mount ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await api(`/ai/coach-report/latest${accountParam}`);
        if (cancelled) return;
        if (data.report) setReport(data.report);
        setCanGenerate(data.canGenerate ?? true);
        setWaitLabel(data.waitLabel ?? null);
        if (data.nextAllowedAt) setNextAllowedAt(new Date(data.nextAllowedAt).getTime());
        else setNextAllowedAt(null);
        setPlan(data.plan ?? 'free');
        setGeneratedAt(data.generatedAt ?? null);
      } catch (err) {
        if (!cancelled) setError('Could not load report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeAccountId]);

  // ── Generate report ──
  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const body = activeAccountId ? { accountId: activeAccountId } : {};
      const data = await api('/ai/coach-report', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (data.insufficient) {
        setError(data.message);
        return;
      }
      if (data.report) {
        setReport(data.report);
        setCanGenerate(data.canGenerate ?? false);
        setWaitLabel(data.waitLabel ?? null);
        if (data.nextAllowedAt) setNextAllowedAt(new Date(data.nextAllowedAt).getTime());
        else setNextAllowedAt(null);
        setPlan(data.plan ?? 'free');
        setGeneratedAt(data.generatedAt ?? new Date());
      }
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Analysis failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [activeAccountId]);

  // ── Fetch weekly ──
  const fetchWeekly = useCallback(async () => {
    setWeeklyLoading(true);
    setWeeklyError(null);
    try {
      const data = await api(`/ai/weekly-report${accountParam}`);
      setWeekly(data);
    } catch (err) {
      setWeeklyError('Could not load weekly digest.');
    } finally {
      setWeeklyLoading(false);
    }
  }, [activeAccountId]);

  // Auto-fetch weekly on tab activate
  useEffect(() => {
    if (activeTab === 'weekly' && !weekly && !weeklyLoading) {
      fetchWeekly();
    }
  }, [activeTab]);

  // ── Fetch report history ──
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await api(`/ai/coach-report/history${accountParam}`);
      setHistoryReports(data.history ?? []);
    } catch (err) {
      setHistoryError('Could not load report history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [activeAccountId]);

  // Auto-fetch history on tab activate (once per mount)
  useEffect(() => {
    if (activeTab === 'history' && historyReports === null && !historyLoading) {
      fetchHistory();
    }
  }, [activeTab]);

  // ── Render ──
  return (
    <div className="kfx-os-page ac-page-root">

      {/* ── Loading full-page ── */}
      {(loading || generating) && (
        <div className="kfx-content ac-content-top">
          <LoadingSkeleton />
        </div>
      )}

      {/* ── No report yet ── */}
      {!loading && !generating && !report && (
        <div className="kfx-content ac-content-top">
          <GenerateState
            onGenerate={handleGenerate}
            loading={generating}
            error={error}
            canGenerate={canGenerate}
            waitLabel={waitLabel}
            nextAllowedAt={nextAllowedAt}
            plan={plan}
            onUpgrade={onUpgrade}
            requiredPlans={requiredPlans}
          />
        </div>
      )}

      {/* ── Report view ── */}
      {!loading && !generating && report && (
        <>
          {/* Mission Control Hero — always above the fold */}
          <MissionControlHero report={report} name={userName} plan={plan} />

          {/* Tab navigation */}
          <div className="ac-tab-nav">
            <div className="kfx-content">
              <div className="ac-tab-strip">
                {TABS.map(tab => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      className={`ac-tab${active ? ' ac-tab--active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  );
                })}

                {/* Metadata right-aligned */}
                <div className="ac-tab-meta">
                  {viewingHistoric && (
                    <span className="ac-tab-historic-badge">
                      <span className="ac-tab-historic-icon">◑</span>
                      Historic — {new Date(viewingHistoric.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      <button
                        className="ac-tab-latest-btn"
                        onClick={() => {
                          setViewingHistoric(null);
                          setReport(null);
                          setLoading(true);
                          api(`/ai/coach-report/latest${accountParam}`)
                            .then(d => { if (d.report) setReport(d.report); setGeneratedAt(d.generatedAt ?? null); })
                            .catch(() => {})
                            .finally(() => setLoading(false));
                        }}
                      >
                        ← Latest
                      </button>
                    </span>
                  )}
                  {!viewingHistoric && generatedAt && (
                    <span className="ac-tab-date">
                      {new Date(generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                  {!viewingHistoric && !canGenerate && mainCountdown && (
                    <span className="ac-tab-countdown" title={`Next report in ${mainCountdown}`}>
                      <span className="ac-tab-countdown-icon">◷</span>
                      <span className="ac-tab-countdown-value">
                        {mainCountdown.split(' ').slice(0, 2).join(' ')}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tab content */}
          <div className="kfx-content ac-tab-content">

            {/* ── MISSION CONTROL ── */}
            {activeTab === 'mission' && (
              <div className="ac-tab-sections">
                {/* Profit Leak Detector */}
                <div>
                  <div className="ac-section-heading">
                    <span className="ac-section-dot ac-section-dot--red">●</span>
                    <span className="ac-section-label">Profit Leak Detector</span>
                  </div>
                  <ProfitLeakDetector report={report} plan={plan} onUpgrade={onUpgrade} requiredPlans={requiredPlans} featureLabel={featureLabel} />
                </div>

                {/* WOW section */}
                <div>
                  <WowSection report={report} />
                </div>

                {/* DNA Profile */}
                <div>
                  <div className="ac-section-heading">
                    <span className="ac-section-dot ac-section-dot--gold">◈</span>
                    <span className="ac-section-label">Trading DNA</span>
                  </div>
                  <DnaProfile report={report} plan={plan} onUpgrade={onUpgrade} requiredPlans={requiredPlans} featureLabel={featureLabel} />
                </div>

                {/* Benchmarks */}
                <div>
                  <div className="ac-section-heading">
                    <span className="ac-section-dot ac-section-dot--blue">◎</span>
                    <span className="ac-section-label">Benchmarking</span>
                  </div>
                  <BenchmarkSection report={report} plan={plan} onUpgrade={onUpgrade} requiredPlans={requiredPlans} featureLabel={featureLabel} />
                </div>

                {/* Regen / upgrade footer */}
                <div className="ac-regen-footer">
                  {canGenerate ? (
                    <button className="kfx-btn-ghost ac-btn-ghost" onClick={handleGenerate} disabled={generating}>
                      ↺ Regenerate Analysis
                    </button>
                  ) : (
                    <div className="ac-regen-wait">
                      <span className="ac-regen-countdown">
                        Next report in <span className="ac-generate-countdown-value">{mainCountdown ?? waitLabel}</span>
                      </span>
                      {!planCanAccess(plan, requiredPlans('ai_unlimited_reports')) && (
                        <button onClick={onUpgrade} className="kfx-btn-primary ac-btn-primary ac-btn-primary--sm">
                          ★ Upgrade for Unlimited Reports
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── MISSIONS ── */}
            {activeTab === 'missions' && (
              <div className="ac-tab-sections">
                <div>
                  <div className="ac-missions-intro">
                    <h2 className="ac-missions-title">Active Missions</h2>
                    <p className="ac-missions-desc">
                      Complete these missions to improve your Edge Score and build lasting trading habits.
                    </p>
                  </div>
                  <MissionSystem report={report} savedMissions={missions} onMissionUpdate={saveMission} />
                </div>

                {/* Strengths & weaknesses */}
                {(report.strengths?.length || report.weaknesses?.length) && (
                  <div className="kfx-two-col">
                    {report.strengths?.length > 0 && (
                      <div className="ac-sw-card ac-sw-card--strengths">
                        <Label className="ac-c-gold ac-mb-14">✓ Your Strengths</Label>
                        {report.strengths.map((s, i) => (
                          <div key={i} className={`ac-sw-item${i < report.strengths.length - 1 ? ' ac-sw-item--border' : ''}`}>
                            <div className="ac-sw-item-title">{s.title}</div>
                            <div className="ac-sw-item-evidence">{humaniseClusterText(s.evidence)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {report.weaknesses?.length > 0 && (
                      <div className="ac-sw-card ac-sw-card--weaknesses">
                        <Label className="ac-c-red ac-mb-14">✗ Weaknesses to Fix</Label>
                        {report.weaknesses.map((w, i) => (
                          <div key={i} className={`ac-sw-item${i < report.weaknesses.length - 1 ? ' ac-sw-item--border' : ''}`}>
                            <div className="ac-sw-item-title">{w.title}</div>
                            <div className="ac-sw-item-evidence">{humaniseClusterText(w.evidence)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* What-if simulations */}
                {report.whatIfAnalysis?.length > 0 && (
                  <div>
                    <div className="ac-section-heading">
                      <span className="ac-section-dot ac-section-dot--amber">◎</span>
                      <span className="ac-section-label">What-If Simulations</span>
                    </div>
                    <WhatIfSection report={report} plan={plan} onUpgrade={onUpgrade} requiredPlans={requiredPlans} featureLabel={featureLabel} />
                  </div>
                )}
              </div>
            )}

            {/* ── DEEP INSIGHTS ── */}
            {activeTab === 'insights' && (
              <div className="ac-tab-sections">
                {/* Executive summary */}
                {report.executiveSummary && (
                  <div className="ac-exec-summary">
                    <Label className="ac-c-gold ac-mb-10">Executive Summary</Label>
                    <p className="ac-exec-summary-text">{report.executiveSummary}</p>
                  </div>
                )}

                {/* Insights */}
                <div>
                  <div className="ac-section-heading">
                    <span className="ac-section-dot ac-section-dot--red">⚠</span>
                    <span className="ac-section-label">All Insights</span>
                    <span className="ac-section-count">{report.insights?.length || 0} found</span>
                  </div>
                  <InsightsList insights={report.insights} plan={plan} onUpgrade={onUpgrade} requiredPlans={requiredPlans} featureLabel={featureLabel} />
                </div>

                {/* Hidden patterns */}
                {report.hiddenPatterns?.length > 0 && (
                  <Disclosure title="Hidden Patterns" icon="◈" badge={`${report.hiddenPatterns.length}`} defaultOpen={false}>
                    <div className="ac-pattern-list">
                      {report.hiddenPatterns.map((p, i) => (
                        <div key={i} className="ac-pattern-card">
                          <div className="ac-pattern-title">{p.pattern}</div>
                          <div className="ac-pattern-evidence">{humaniseClusterText(p.evidence)}</div>
                          <div className="ac-pattern-implication">{p.implication}</div>
                        </div>
                      ))}
                    </div>
                  </Disclosure>
                )}

                {/* Behavioral mistakes */}
                {report.behavioralMistakes?.length > 0 && (
                  <Disclosure title="Behavioral Mistakes" icon="◉" badge={`${report.behavioralMistakes.length}`} defaultOpen={false}>
                    <div className="ac-pattern-list">
                      {report.behavioralMistakes.map((m, i) => (
                        <div key={i} className="kfx-lift ac-mistake-card">
                          <div className="ac-mistake-head">
                            <span className="ac-mistake-title">{m.mistake}</span>
                            {m.frequency && <span className="ac-mistake-freq">{m.frequency}</span>}
                            {m.cost && <span className="ac-mistake-cost ac-c-red">{humaniseImpact(m.cost)}</span>}
                          </div>
                          {m.fix && <div className="ac-mistake-fix">Fix: {m.fix}</div>}
                        </div>
                      ))}
                    </div>
                  </Disclosure>
                )}
              </div>
            )}

            {/* ── MARKETS & SESSIONS ── */}
            {activeTab === 'markets' && (
              <MarketSection report={report} />
            )}

            {/* ── WEEKLY DIGEST ── */}
            {activeTab === 'weekly' && (
              <WeeklyDigestSection
                weekly={weekly}
                onFetch={fetchWeekly}
                loading={weeklyLoading}
                plan={plan}
              />
            )}

            {/* ── REPORT HISTORY ── */}
            {activeTab === 'history' && (
              <div className="ac-history-root">
                <div className="ac-section-heading ac-mb-8">
                  <span className="ac-section-dot ac-section-dot--gold ac-section-dot--dim">◑</span>
                  <span className="ac-section-label">Past Analyses</span>
                  <button
                    onClick={fetchHistory}
                    disabled={historyLoading}
                    className={`ac-history-refresh${historyLoading ? ' ac-history-refresh--disabled' : ''}`}
                  >
                    {historyLoading ? 'Loading…' : '↺ Refresh'}
                  </button>
                </div>

                {historyLoading && (
                  <div className="ac-history-status">Loading history…</div>
                )}

                {historyError && (
                  <div className="ac-history-error">{historyError}</div>
                )}

                {!historyLoading && !historyError && historyReports !== null && historyReports.length === 0 && (
                  <div className="ac-history-status">No previous analyses found.</div>
                )}

                {!historyLoading && historyReports?.map((entry, i) => {
                  const isActive = viewingHistoric?.id === entry.id;
                  const gradeTone = GRADE_TONE[entry.grade] || 'gold';
                  return (
                    <div key={entry.id} className={`ac-history-card${isActive ? ' ac-history-card--active' : ''}`}>
                      {/* Index badge */}
                      <span className="ac-history-idx">
                        {i === 0 ? '★' : `#${i + 1}`}
                      </span>

                      {/* Date */}
                      <div className="ac-history-date-col">
                        <div className="ac-history-date">{new Date(entry.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        <div className="ac-history-time">{new Date(entry.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>

                      {/* Trade count */}
                      <div className="ac-history-stat">
                        <div className="ac-history-stat-val">{entry.tradeCount ?? '—'}</div>
                        <div className="ac-history-stat-label">Trades</div>
                      </div>

                      {/* Edge score */}
                      {entry.edgeScore != null && (
                        <div className="ac-history-stat">
                          <div className="ac-history-stat-val ac-c-gold">{entry.edgeScore}</div>
                          <div className="ac-history-stat-label">Edge Score</div>
                        </div>
                      )}

                      {/* Grade */}
                      {entry.grade && (
                        <div className="ac-history-stat">
                          <div className={`ac-history-grade ac-history-grade--${gradeTone}`}>{entry.grade}</div>
                          <div className="ac-history-stat-label">Grade</div>
                        </div>
                      )}

                      {/* View button */}
                      <button
                        onClick={() => {
                          if (isActive) return;
                          setReport(entry.reportJson);
                          setGeneratedAt(entry.createdAt);
                          setViewingHistoric({ id: entry.id, createdAt: entry.createdAt });
                          setActiveTab('mission');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`ac-history-view-btn${isActive ? ' ac-history-view-btn--active' : ''}`}
                      >
                        {isActive ? '✓ Viewing' : 'View →'}
                      </button>
                    </div>
                  );
                })}

                {!historyLoading && historyReports !== null && historyReports.length > 0 && (
                  <div className="ac-history-footer">
                    Showing last {historyReports.length} report{historyReports.length !== 1 ? 's' : ''}. Generate a new analysis to add to this history.
                  </div>
                )}
              </div>
            )}

          </div>
        </>
      )}
    </div>
  );
}